import React, { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useCompanyTypes } from "@hooks/useCompanyTypes";
import { formatCompanyTypes } from "@pages/vendor-license-request/constants";
import type { ServiceDeskLicensesSearchResponse } from "@services/licensing/types";
import { searchServiceDeskLicenses } from "@services/licensingApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNTS_FIELDS,
  buildBodyFromFilters,
  formatDateTime,
  formatLicenseType,
  formatStatus,
  getCompanyTypeFilterField,
  getLicenseBadgeType,
  getLicenseTypeUrlValue,
} from "./utils";

const LANG_BASE = "licensing";
const TEST_ID_BASE = "accounts";

const LOCAL_STORAGE_PAGE_KEY = "accounts-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "accounts-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "accounts-filters";
const SEARCH_INQUIRY_ID = "licensing.accounts.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "licensing",
  screen: "Accounts",
  action: "search_vendor_licenses",
  entityType: "license",
};

const ACCOUNTS_STATUSES = ["PENDING", "APPROVED", "REJECTED", "EXPIRED"] as const;
const ACCOUNTS_LICENSE_TYPES = ["VENDOR_LICENSE", "PORT_OPERATOR_LICENSE"] as const;

interface AccountsProps {
  onIsLoading: (v: boolean) => void;
}

export const Accounts: React.FC<AccountsProps> = ({ onIsLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: companyTypes = [] } = useCompanyTypes();

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });
  const {
    data,
    isLoading: isLicensesLoading,
    isFetching: isLicensesFetching,
    isError: isLicensesError,
    isSuccess: isLicensesSuccess,
    dataUpdatedAt: licensesDataUpdatedAt,
    error,
  } = useQuery<ServiceDeskLicensesSearchResponse>({
    queryKey: ["serviceDeskLicenses", page, pageSize, filters],
    queryFn: () =>
      searchServiceDeskLicenses({
        page,
        pageSize,
        body: buildBodyFromFilters(filters),
      }),
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: isLicensesFetching,
    isLoading: isLicensesLoading,
    isError: isLicensesError,
    isSuccess: isLicensesSuccess,
    error,
    dataUpdatedAt: licensesDataUpdatedAt,
  });
  useEffect(() => {
    onIsLoading(isLicensesLoading);

    return () => onIsLoading(false);
  }, [onIsLoading, isLicensesLoading]);

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isLicensesLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Licenses");
    trackScreenLoad("Licenses", "Accounts", startedAt.current);
  }, [error, isLicensesLoading]);

  const headers: Header[] = useMemo(() => {
    return Object.values(ACCOUNTS_FIELDS).map((field) => ({
      field,
      columnName: t(`${LANG_BASE}.headerLabels.${field}`),
    }));
  }, [t]);

  const filterFields: Record<string, FilterFieldDefinition> = useMemo(
    () => ({
      refOrderNumber: {
        label: t(`${LANG_BASE}.headerLabels.requestedId`),
        type: "text",
        minLength: 1,
      },
      licenseType: {
        label: t(`${LANG_BASE}.headerLabels.licenseType`),
        type: "autocomplete",
        options: ACCOUNTS_LICENSE_TYPES.map((licenseType) => ({
          label: formatLicenseType(licenseType),
          value: licenseType,
        })),
      },
      companyName: {
        label: t(`${LANG_BASE}.headerLabels.companyName`),
        type: "text",
        minLength: 1,
      },
      companyType: getCompanyTypeFilterField(
        t(`${LANG_BASE}.headerLabels.companyType`),
        companyTypes,
      ),
      natureOfBusinessInPof: {
        label: t(`${LANG_BASE}.headerLabels.natureOfBusiness`),
        type: "text",
        minLength: 5,
      },
      tradingLicenseNumber: {
        label: t(`${LANG_BASE}.headerLabels.tradeLicense`),
        type: "text",
        minLength: 1,
      },
      createdAt: {
        label: t(`${LANG_BASE}.headerLabels.dateSubmitted`),
        type: "dateFromToWithoutTime",
      },
      status: {
        label: t(`${LANG_BASE}.headerLabels.status`),
        type: "autocomplete",
        options: ACCOUNTS_STATUSES.map((status) => ({
          label: t(`${LANG_BASE}.status-labels.${status}`),
          value: status,
        })),
      },
    }),
    [companyTypes, t],
  );

  const onApplyFilters = (filters: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(filters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };
  const na = useMemo(() => t("common.notApplicable"), [t]);

  const items = data?.items ?? [];
  const formattedFormData = items.map((input) => ({
    id: input.id,
    rawLicenseType: input.licenseType,
    requestedId: input.refOrderNumber ?? na,
    licenseType: input.licenseType ? formatLicenseType(input.licenseType) : na,
    companyName: input.companyName ?? na,
    companyType: formatCompanyTypes(input.companyTypes) ?? na,
    natureOfBusiness: input.natureOfBusinessInPof ?? na,
    tradeLicense: input.tradingLicenseNumber ?? na,
    dateSubmitted: formatDateTime(input.createdAt),
    status: formatStatus(input.status, t) ?? na,
    badgeType: getLicenseBadgeType(input.status),
  }));

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={error.message} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      <CustomDataTable
        testId={TEST_ID_BASE}
        actionName={t(`${LANG_BASE}.add-license`)}
        headers={headers}
        rows={formattedFormData}
        page={page}
        title={" "}
        pageSize={pageSize}
        totalCount={data?.totalElements ?? 0}
        canPreview={true}
        onRowClick={(row: { id: string; rawLicenseType?: string }) => {
          const params = new URLSearchParams();
          if (row.rawLicenseType) {
            params.set("licenseType", getLicenseTypeUrlValue(row.rawLicenseType));
          }
          navigate(`license-details/${row.id}${params.toString() ? `?${params.toString()}` : ""}`);
        }}
        onPageChange={(newPage, newSize) => {
          if (newPage !== page || newSize !== pageSize) {
            startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
          }
          setPage(newPage);
          setPageSize(newSize);
          localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
          localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
        }}
        navigationLinkBase="license-details"
        navigationParam="id"
        pageSizeOptions={[5, 10, 15, 20, 25]}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        availableFields={filterFields}
      />
    </div>
  );
};

export default Accounts;
